import {
  useAccount,
  useReadContracts,
  useSwitchChain,
  useWriteContract,
} from 'wagmi';
import erc20ABI from '@/lib/abi/ERC20.json';
import { useMemo } from 'react';
import { HYPER_DEPOSIT_CONTRACT } from '@/lib/config/contracts';
import { formatToBn, parseToBN } from '@/lib/utils/number.utils';
import BigNumber from 'bignumber.js';
import { HexString, wagmiConfig } from '@/lib/config/wagmi';
import { waitForTransactionReceipt } from '@wagmi/core';
import { arbitrum } from 'wagmi/chains';
type UseErc20TokenBalanceParams = {
  contractAddress: string | HexString;
  userAddress: string | HexString | undefined;
  toAddress?: string | HexString;
  _chainId?: number;
};

export const useErc20TokenBalance = ({
  contractAddress,
  userAddress,
  toAddress = HYPER_DEPOSIT_CONTRACT,
  _chainId = arbitrum.id,
}: UseErc20TokenBalanceParams) => {
  const { switchChainAsync } = useSwitchChain();
  const { chainId } = useAccount();
  const baseContractInfo = useMemo(() => {
    return {
      address: contractAddress as HexString,
      abi: erc20ABI,
    };
  }, [contractAddress]);
  const { data, refetch: tokenInfoRefresh } = useReadContracts({
    contracts: [
      {
        ...baseContractInfo,
        functionName: 'decimals',
      },
      {
        ...baseContractInfo,
        functionName: 'balanceOf',
        args: [userAddress],
      },
      {
        ...baseContractInfo,
        functionName: 'allowance',
        args: [userAddress, toAddress],
      },
    ],
  });
  const decimals = Number(data?.[0].result ?? 18);
  const balance = parseToBN((data?.[1].result ?? 0).toString(), decimals);
  const allowance = parseToBN(
    (data?.[1].result ?? 0).toString() ?? 0,
    decimals,
  );
  const { writeContractAsync } = useWriteContract();
  const approve = async (
    amount: BigNumber,
    _toAddress?: string | HexString,
  ) => {
    const res = await writeContractAsync({
      abi: erc20ABI,
      address: contractAddress as HexString,
      functionName: 'approve',
      args: [_toAddress ?? toAddress, formatToBn(amount, decimals)],
    });

    const waitRes = await waitForTransactionReceipt(wagmiConfig, {
      hash: res,
    });
    if (waitRes.status === 'success') {
      await tokenInfoRefresh();
    }
    return waitRes;
  };
  const transfer = async (
    amount: BigNumber,
    _toAddress?: string | HexString,
  ) => {
    if (chainId !== _chainId) {
      await switchChainAsync({ chainId: _chainId });
    }
    if (allowance.isLessThan(amount)) {
      throw new Error('Insufficient allowance');
    }
    const res = await writeContractAsync({
      abi: erc20ABI,
      address: contractAddress as HexString,
      functionName: 'transfer',
      args: [_toAddress ?? toAddress, formatToBn(amount, decimals)],
    });
    return waitForTransactionReceipt(wagmiConfig, {
      hash: res,
    });
  };
  // console.log(data?.[0].result ?? 18, Number(data?.[0].result ?? 18));

  return {
    decimals,
    balance,
    allowance,
    approve,
    transfer,
  };
};
